import { assign, groupBy } from 'lodash'
import * as api from '@/api'
import { errorParser } from '@/helpers'
import { useJobsStore } from '@/stores'

export const initialState = () => ({
  count: null,
  selection: {}
})

export const mutations = {
  set (state, { count, results }) {
    if (count) state.count = count
    const newSelection = { ...state.selection }
    for (const [corpusId, elements] of Object.entries(groupBy(results, element => element.corpus.id))) {
      const elementIds = elements.map(element => element.id)
      if (Array.isArray(newSelection[corpusId])) {
        newSelection[corpusId] = [
          ...new Set([...newSelection[corpusId], ...elementIds])
        ]
      } else newSelection[corpusId] = elementIds
    }
    state.selection = newSelection
  },
  remove (state, element) {
    const newSelection = { ...state.selection }
    if (newSelection[element.corpus.id].includes(element.id)) {
      newSelection[element.corpus.id] = newSelection[element.corpus.id].filter(id => id !== element.id)
      state.selection = newSelection
      state.count--
    }
  },
  removeCorpus (state, corpusId) {
    const newSelection = { ...state.selection }
    const removedCount = newSelection[corpusId].length
    delete newSelection[corpusId]
    state.selection = newSelection
    state.count -= removedCount
  },
  reset (state) {
    assign(state, initialState())
  }
}

export const actions = {
  async get ({ commit }, payload) {
    commit('reset')
    let page = payload.page || 1
    let data
    do {
      try {
        data = await api.listSelection({ ...payload, page })
        commit('elements/bulkSet', data.results, { root: true })
        commit('set', data)
        page += 1
      } catch (err) {
        commit('reset')
        commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
        throw err
      }
    } while (data.next)
  },

  async select ({ commit }, { elements }) {
    try {
      await api.addSelection(elements.map(e => e.id))
      /*
       * TODO: Remove after the elements refactoring is completed
       * While the selection module already fully uses elements,
       * parts of the elements sent to this action can still come from the old elements module;
       * in this case, selecting elements will work but they will not be displayed,
       * since they might not exist in elements's state.
       * We fix this by just asking elements to ensure they are in there.
       */
      commit('elements/bulkSet', elements, { root: true })
      commit('set', { results: elements })
    } catch (err) {
      commit('reset')
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
      throw err
    }
  },

  async unselect ({ commit }, element) {
    try {
      await api.removeSelection(element.id)
      commit('remove', element)
    } catch (err) {
      commit('reset')
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
      throw err
    }
  },

  async clear ({ commit }) {
    try {
      await api.clearSelection()
    } catch (err) {
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
      throw err
    } finally {
      commit('reset')
    }
  },

  async validateClassifications ({ commit }, { corpusId }) {
    try {
      await api.validateClassificationsSelection(corpusId)
    } catch (err) {
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
      throw err
    }
  },

  async createClassifications ({ commit }, { corpusId, mlClassId }) {
    try {
      await api.createClassificationsSelection(corpusId, mlClassId)
    } catch (err) {
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
      throw err
    }
  },

  async deleteElements ({ commit }, { corpusId }) {
    try {
      await api.deleteElementsSelection(corpusId)
      commit('notifications/notify', { type: 'success', text: 'Elements deletion has been scheduled.' }, { root: true })
      commit('removeCorpus', corpusId)
    } catch (err) {
      commit('notifications/notify', { type: 'error', text: errorParser(err) }, { root: true })
    } finally {
      useJobsStore().list()
    }
  },

  async moveSelection ({ commit }, { corpusId, destination }) {
    try {
      await api.moveSelection({ corpus_id: corpusId, destination })
      commit('notifications/notify', { type: 'success', text: 'Elements moving has been scheduled.' }, { root: true })
    } catch (err) {
      const message = err.response?.data?.destination ?? err.response?.data?.corpus_id ?? err
      commit('notifications/notify', { type: 'error', text: errorParser(message) }, { root: true })
    } finally {
      useJobsStore().list()
    }
  },

  async createParent ({ commit }, { corpusId, parentId }) {
    try {
      await api.createParentSelection({ corpus_id: corpusId, parent_id: parentId })
      commit('notifications/notify', { type: 'success', text: 'Element linking has been scheduled.' }, { root: true })
    } catch (err) {
      const message = err.response?.data?.parent_id ?? err.response?.data?.corpus_id ?? err
      commit('notifications/notify', { type: 'error', text: errorParser(message) }, { root: true })
    } finally {
      useJobsStore().list()
    }
  }
}

export const getters = {
  loaded: state => Object.values(state.selection).reduce((total, array) => { total += array.length; return total }, 0)
}

export default {
  namespaced: true,
  state: initialState(),
  mutations,
  actions,
  getters
}
